import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "enablePopovers", "inputMode", "isNewEmail", "isOnlyEmail", "isPrimaryEmail", "objectTypeId", "onDelete", "onPromoteToPrimary", "onSave", "value", "showAutoSaveText", "aria-labelledby", "aria-label", "data-test-id"];
import { createRef, useCallback, useState } from 'react';
import { Box } from 'foundations-components/transitional/components/Box';
import PropertyInputEmailV2 from '../../PropertyInputEmailV2';
import { EditableControls } from 'foundations-components/transitional/components/EditableControls';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { usePropertyValidation } from '../../../validation/hooks/usePropertyValidation';
import emptyFunction from 'react-utils/emptyFunction';
import { useObjectId } from '../../../v2/hooks/useObjectId';
import { useProperty } from '../../../v2/hooks/useProperty';
import { Flex } from 'foundations-components/components/Flex';
import { filterSpaceProps } from '../../../utils/filterSpaceProps';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SingleEmailInputWithControls = _ref => {
  let {
      disabled,
      enablePopovers,
      inputMode,
      isNewEmail,
      isOnlyEmail,
      isPrimaryEmail,
      objectTypeId,
      onDelete,
      onPromoteToPrimary,
      onSave,
      value,
      showAutoSaveText,
      'aria-labelledby': ariaLabelledby,
      'aria-label': ariaLabel,
      'data-test-id': dataTestId
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    spaceProps
  } = filterSpaceProps(restProps);

  // We use a draftValue of undefined when no changes are being made because the
  // source of truth for value is the "value" prop. The draft is only used as
  // the value when the user is editing the email, as soon as they save or
  // cancel we set it back to undefined.
  const [draftValue, setDraftValue] = useState(undefined);
  const currentValue = draftValue === undefined ? value : draftValue;
  const property = useProperty();
  const objectId = useObjectId();
  const handleDraftChange = useCallback(changes => {
    const newEmailValue = changes[property.name];
    if (newEmailValue != null) {
      setDraftValue(Object.assign({}, currentValue, {
        value: newEmailValue
      }));
    }
  }, [currentValue, property.name]);
  const {
    onChangeSingle,
    onChangeMultiple,
    validationState: {
      saveable,
      invalid,
      message
    }
  } = usePropertyValidation({
    objectId,
    objectTypeId,
    onChange: handleDraftChange,
    // No `onValidationChange` needed because we don't broadcast the result of
    // this validation to our parent component. Validation is entirely internal
    // to this component
    onValidationChange: emptyFunction,
    property,
    propertyValue: currentValue.value
  });
  const inputRef = /*#__PURE__*/createRef();
  const handleDelete = useCallback(() => {
    onDelete(currentValue);
  }, [onDelete, currentValue]);
  const handlePromoteToPrimary = useCallback(() => {
    onPromoteToPrimary(currentValue);
  }, [currentValue, onPromoteToPrimary]);
  const isSaveDisabled = !saveable || draftValue === undefined || draftValue.value === '';
  const handleSave = useCallback(() => {
    // UIEditableControls' saveDisabled props should prevent users from clicking
    // the Save button or pressing Enter to save while draftValue is undefined.
    // However we see Sentries from Win10+Chrome users who are able to trigger
    // the save callback anyway (possibly related to touch devices).
    // See https://git.hubteam.com/HubSpot/PropertiesFrontendTeam/issues/1031
    // See https://git.hubteam.com/HubSpot/PropertiesFrontendTeam/issues/1385
    if (isSaveDisabled) {
      return;
    }
    onSave(draftValue, value);
    setDraftValue(undefined);
  }, [draftValue, onSave, value, isSaveDisabled]);
  const handleCancel = useCallback(() => {
    if (isNewEmail) {
      onDelete(currentValue);
    } else {
      setDraftValue(undefined);
    }
  }, [currentValue, isNewEmail, onDelete]);
  const isDeleteButtonDisabled = isPrimaryEmail && !isOnlyEmail || disabled;
  const isMakePrimaryButtonDisabled = draftValue !== undefined || disabled;
  const confirmLabel = showAutoSaveText ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputMultipleEmailV2.save"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputMultipleEmailV2.apply"
  });
  return /*#__PURE__*/_jsxs(Flex, Object.assign({
    flexWrap: "wrap",
    "data-test-id": dataTestId
  }, spaceProps, {
    children: [/*#__PURE__*/_jsx(Box, {
      grow: 1,
      marginInlineEnd: "400",
      marginBlock: "100",
      children: /*#__PURE__*/_jsx(EditableControls, {
        onCancel: handleCancel,
        onSave: handleSave,
        saveDisabled: isSaveDisabled,
        use: "flush",
        saveLabel: confirmLabel,
        margin: "0",
        children: /*#__PURE__*/_jsx(FormControl, {
          error: invalid,
          validationMessage: message,
          "aria-labelledby": ariaLabelledby,
          "aria-label": ariaLabel,
          children: /*#__PURE__*/_jsx(PropertyInputEmailV2, {
            disabled: disabled,
            enablePopovers: enablePopovers,
            inputMode: inputMode,
            inputRef: inputRef,
            onChange: onChangeSingle,
            onChangeMultiple: onChangeMultiple,
            onValidationChange: emptyFunction,
            popoverOpen: false,
            setPopoverOpen: emptyFunction,
            shouldOverrideValue: true,
            overrideValue: currentValue.value,
            formControlUse: "default"
          })
        })
      })
    }), !isNewEmail && /*#__PURE__*/_jsx(Box, {
      alignSelf: "start",
      shrink: 0,
      marginBlock: "100",
      children: /*#__PURE__*/_jsxs(ButtonWrapper, {
        children: [!isPrimaryEmail && /*#__PURE__*/_jsx(Tooltip, {
          "data-test-id": "multiple-email-make-primary-tooltip",
          disabled: !isMakePrimaryButtonDisabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputMultipleEmailV2.makePrimary.disabledTooltip"
          }),
          children: /*#__PURE__*/_jsx(Button, {
            "data-test-id": "multiple-email-make-primary-btn",
            disabled: isMakePrimaryButtonDisabled,
            onClick: handlePromoteToPrimary,
            use: "tertiary-light",
            size: "sm",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputMultipleEmailV2.makePrimary.buttonText"
            })
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          "data-test-id": "multiple-email-delete-tooltip",
          disabled: !isDeleteButtonDisabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.disabledTooltip"
          }),
          children: /*#__PURE__*/_jsx(Button, {
            "data-test-id": "multiple-email-delete-btn",
            disabled: isDeleteButtonDisabled,
            onClick: handleDelete,
            use: "danger",
            size: "sm",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.buttonText"
            })
          })
        })]
      })
    })]
  }));
};
export default SingleEmailInputWithControls;